import { Button } from '@/components/ui/Button';
import { ChartNoAxesColumn, Image, Trash } from 'lucide-react';
import React, { useEffect, useState } from 'react';

import { ButtonLink } from '@/components/ui/Link';
import config from '@/config';
import {
  AuditLog,
  AuditLogBoolExp,
  AuditLogInsertInput,
  AuditLogOrderBy,
  Customer,
  Device,
  DevicePkColumnsInput,
  DeviceSetInput,
  OrderBy,
  Roi,
} from '@/generated/graphql';
import { execute } from '@/graphql/execute';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router';
import {
  auditLogByEntityQuery,
  customerByDeviceQuery,
  deviceByPkQuery,
  deviceByPkQueryNoApiKeyDescription,
  roisByDeviceQuery,
} from '../graphql/query';

import { useConfirmModal } from '@/components/ConfirmModalProvider';
import { ErrorBoundaryFallbackRender } from '@/components/ui/ErrorBoundaryFallback';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/Select';
import { Spinner } from '@/components/ui/Spinner';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/Tabs';
import { AuditLogAction, AuditLogEntityType } from '@/models/audit-log.models';
import { CustomerConfig } from '@/models/customer.models';
import { JSONConfig, MediaKind, Product } from '@/models/device.models';
import { FeatureName } from '@/models/role.models';
import { SiteConfig } from '@/models/site.models';
import { createAuditLogInput } from '@/utils/audit-log.function';
import hasPermission from '@/utils/permission.function';
import { ErrorBoundary } from 'react-error-boundary';
import { toast } from 'sonner';
import { v4 as uuid } from 'uuid';
import { DeviceApiKeysTab } from '../components/edit-device-tabs/DeviceApiKeysTab';
import { DeviceAssignedUserTab } from '../components/edit-device-tabs/DeviceAssignedUserTab';
import { AuditHistoryTab } from '../components/edit-device-tabs/DeviceAuditHistoryTab';
import { DeviceConfigTab } from '../components/edit-device-tabs/DeviceConfigTab';
import { DeviceGeneralTab } from '../components/edit-device-tabs/DeviceGeneralTab';
import {
  deleteDeviceMutation,
  refreshDeviceCacheMutation,
  updateDeviceMutation,
} from '../graphql/mutation';

type AuditLogFilterInput = {
  where: AuditLogBoolExp;
  orderBy: AuditLogOrderBy[];
};

const EditDevicePage: React.FC = () => {
  const { id } = useParams();
  const { openModal } = useConfirmModal();
  const [device, setDevice] = useState<Device>();
  const [product, setProduct] = useState<Product>();

  const handleManageProducts = () => {
    console.log('Manage Products clicked');
  };

  const handleProductSelect = (value: string) => {
    if (value === 'manageProducts') {
      handleManageProducts();
      return;
    }

    setProduct(value as Product);
  };

  const [grafanaUrl, setGrafanaURL] = useState<string>();
  const [auditHistory, setAuditHistory] = useState<AuditLog[]>();
  const [editingGeneralTab, setEditingGeneralTab] = useState<boolean>(false);
  const [selectedMediaTimestamp, setSelectedMediaTimestamp] =
    useState<string>();
  const [selectedMediaKind, setSelectedMediaKind] = useState<MediaKind>();
  const navigate = useNavigate();

  const [auditLogFilters, _setAuditLogFilters] = useState<AuditLogFilterInput>({
    orderBy: [
      {
        createdAt: OrderBy.Desc,
      },
    ],
    where: {
      entities: {
        _contains: [
          {
            id: id,
          },
        ],
      },
    },
  });

  const { data: deviceData, refetch: refetchDevice } = useQuery({
    queryKey: ['Device', id],
    queryFn: () =>
      execute({
        query: config.apiKey.hasDescription
          ? deviceByPkQuery
          : deviceByPkQueryNoApiKeyDescription,
        variables: {
          id: id as string,
        },
      }),
    select: (data) => data,
    retry: false,
    staleTime: 5000,
  });

  const { data: deviceCustomerData } = useQuery({
    queryKey: ['CustomerByDevice', id],
    queryFn: () =>
      execute({
        query: customerByDeviceQuery,
        variables: {
          id: id as string,
        },
      }),
    select: (data) => data,
    retry: false,
    staleTime: 5000,
    enabled: hasPermission(FeatureName.VIEW_GRAFANA_BUTTON),
  });

  const { data: auditLogData, refetch: refetchAuditLog } = useQuery({
    queryKey: ['AuditLog', auditLogFilters],
    queryFn: () =>
      execute({
        query: auditLogByEntityQuery,
        variables: {
          where: auditLogFilters.where,
          orderBy: auditLogFilters.orderBy,
        },
      }),
    select: (data) => data,
    enabled: hasPermission(FeatureName.VIEW_AUDIT_HISTORY),
    staleTime: 3000,
  });

  useEffect(() => {
    if (auditLogData) {
      setAuditHistory(auditLogData.auditLog as AuditLog[]);
    }
  }, [auditLogData]);

  const { data: roiData, refetch: refetchDeviceRois } = useQuery({
    queryKey: ['RoisByDevice', id],
    queryFn: () =>
      execute({
        query: roisByDeviceQuery,
        variables: {
          deviceId: id as string,
        },
      }),
    select: (data) => data,
    retry: false,
    staleTime: 5000,
    enabled: hasPermission(FeatureName.VIEW_ROI_ZONE),
  });

  const { mutateAsync: deleteDevice } = useMutation({
    mutationKey: ['DeleteDevice'],
    mutationFn: ({
      apiKeyIds,
      deviceId,
      auditLogInput,
    }: {
      apiKeyIds: number[];
      deviceId: string;
      auditLogInput: AuditLogInsertInput;
    }) => {
      return execute({
        query: deleteDeviceMutation,
        variables: {
          apiKeyIds,
          deviceId,
          auditLogInput,
        },
      });
    },
    onSuccess: () => {
      void navigate('/devices');
      toast.success('Device deleted successfully!', {
        duration: 3000,
      });
    },
  });

  const { mutateAsync: updateDevice } = useMutation({
    mutationKey: ['UpdateDevice'],
    mutationFn: ({
      devicePkColumnInput,
      setInput,
      auditLogInput,
    }: {
      devicePkColumnInput: DevicePkColumnsInput;
      setInput: DeviceSetInput;
      auditLogInput: AuditLogInsertInput;
    }) => {
      return execute({
        query: updateDeviceMutation,
        variables: {
          devicePkColumnInput,
          setInput,
          auditLogInput,
          invalidateInput: {
            deviceIds: [devicePkColumnInput.id],
          },
        },
      });
    },
    onSuccess: async () => {
      await refetchDevice();
      await refetchAuditLog();
      toast.success('Device updated successfully!', {
        duration: 3000,
      });
    },
  });

  const { isPending: isRefreshingCache, mutateAsync: refreshDeviceCache } =
    useMutation({
      mutationKey: ['RefreshDeviceCache'],
      mutationFn: ({ deviceId }: { deviceId: string }) => {
        return execute({
          query: refreshDeviceCacheMutation,
          auditPolicy: 'exempt',
          variables: {
            invalidateInput: {
              deviceIds: [deviceId],
            },
          },
        });
      },
      onSuccess: async () => {
        await refetchAuditLog();
        toast.success('Device cache refresh requested.', {
          duration: 3000,
        });
      },
    });

  // Effects
  useEffect(() => {
    if (deviceData) {
      setDevice(deviceData.deviceByPk as Device);
      const products = deviceData.deviceByPk?.products as Product[];
      setProduct(products[0]);
    }
  }, [deviceData]);

  useEffect(() => {
    if (deviceCustomerData && device) {
      const customer = (deviceCustomerData.deviceByPk as Device)?.site
        ?.customer as Customer;

      const customerConfig = customer?.config as CustomerConfig;
      const siteConfig = device.site?.config as SiteConfig;
      if (customerConfig?.Grafana) {
        const base = `https://sensen.grafana.net/d/${customerConfig.Grafana?.DashboardUid}/${customerConfig.Grafana?.DashboardSlug}`;
        const params = new URLSearchParams({
          orgId: customerConfig.Grafana?.OrganizationId,
          from: 'now-2d',
          to: 'now',
          timezone: siteConfig?.Timezone ?? '',
          'var-cameraName': `${device.name}_${device.serial}`,
        });
        const grafanaUrl = `${base}?${params.toString()}`;
        setGrafanaURL(grafanaUrl);
      }
    }
  }, [deviceCustomerData, device]);

  const handleConfigChange = async (config: JSONConfig) => {
    const updateInput: DeviceSetInput = {
      updatedAt: new Date().toISOString(),
      rowVersion: uuid(),
      config: config,
    };

    await updateDevice({
      devicePkColumnInput: {
        id: device?.id as string,
      },
      setInput: updateInput,
      auditLogInput: createAuditLogInput({
        action: AuditLogAction.UPDATE_DEVICE,
        entities: [
          {
            id: device?.id as string,
            entity: AuditLogEntityType.DEVICE,
          },
        ],
      }),
    });
  };

  const handleUpdateDevice = async (input: DeviceSetInput) => {
    await updateDevice({
      devicePkColumnInput: {
        id: device?.id as string,
      },
      setInput: input,
      auditLogInput: createAuditLogInput({
        action: AuditLogAction.UPDATE_DEVICE,
        entities: [
          {
            id: device?.id as string,
            entity: AuditLogEntityType.DEVICE,
          },
        ],
      }),
    });
    setEditingGeneralTab(false);
  };

  const handleDeleteDevice = () => {
    openModal(
      {
        message:
          'Are you sure you want to delete this device? This action cannot be undone.',
      },
      () => {
        void deleteDevice({
          apiKeyIds: device?.apiKeys?.map((key) => key.id as number) ?? [],
          deviceId: device?.id as string,
          auditLogInput: createAuditLogInput({
            action: AuditLogAction.DELETE_DEVICE,
            entities: [
              {
                entity: AuditLogEntityType.DEVICE,
                id: device?.id as string,
              },
            ],
          }),
        });
      },
    );
  };

  const handleRefreshCache = async () => {
    await refreshDeviceCache({
      deviceId: device?.id as string,
    });
  };

  return (
    <ErrorBoundary fallbackRender={ErrorBoundaryFallbackRender}>
      {device ? (
        <>
          <div className="flex justify-between align-middle">
            <div className="flex items-center space-x-2">
              <span className="text-xl font-semibold tracking-tight">
                {device.name}
              </span>
            </div>
            <div className="flex gap-2">
              {hasPermission(FeatureName.VIEW_GRAFANA_BUTTON) && (
                <ButtonLink
                  variant={'outline'}
                  size={'sm'}
                  to={grafanaUrl as string}
                  target="_blank"
                  disabled={!grafanaUrl}
                >
                  <ChartNoAxesColumn />
                  Grafana
                </ButtonLink>
              )}
              <ButtonLink
                variant={'outline'}
                size={'sm'}
                to={(() => {
                  const params = new URLSearchParams();
                  if (selectedMediaTimestamp) {
                    params.set('time', selectedMediaTimestamp);
                  }
                  if (selectedMediaKind) {
                    params.set('kind', selectedMediaKind);
                  }
                  const query = params.toString();
                  return query
                    ? `/devices/${device.id}/media?${query}`
                    : `/devices/${device.id}/media`;
                })()}
              >
                <Image />
                Media
              </ButtonLink>
              {hasPermission(FeatureName.DELETE_DEVICE) && (
                <Button
                  variant={'destructive'}
                  size={'sm'}
                  onClick={handleDeleteDevice}
                >
                  <Trash />
                  Delete
                </Button>
              )}
            </div>
          </div>
          <Tabs defaultValue={'general'}>
            <div className="flex flex-col justify-between gap-2 lg:flex-row">
              <TabsList className="flex flex-wrap gap-4">
                <TabsTrigger value="general" className="w-30">
                  General
                </TabsTrigger>
                {hasPermission(FeatureName.VIEW_DEVICE_CONFIG) && (
                  <TabsTrigger value="config" className="w-30">
                    Config
                  </TabsTrigger>
                )}
                {hasPermission(FeatureName.VIEW_API_KEYS) && (
                  <TabsTrigger value="apiKeys" className="w-30">
                    Api Keys
                  </TabsTrigger>
                )}
                {hasPermission(FeatureName.VIEW_ASSIGNED_USERS) && (
                  <TabsTrigger value="assignedUsers" className="w-30">
                    Assigned Users
                  </TabsTrigger>
                )}
                {hasPermission(FeatureName.VIEW_AUDIT_HISTORY) && (
                  <TabsTrigger value="auditHistory" className="w-30">
                    Audit History
                  </TabsTrigger>
                )}
              </TabsList>
              <Select
                onValueChange={handleProductSelect}
                value={product}
                disabled={!hasPermission(FeatureName.UPDATE_DEVICE)}
              >
                <SelectTrigger className="w-[180px] text-sm leading-none font-medium capitalize">
                  <SelectValue placeholder="Select a product" />
                </SelectTrigger>
                <SelectContent>
                  <SelectGroup>
                    <SelectLabel>Products</SelectLabel>
                    {(device?.products as Product[])?.map((prod) => (
                      <SelectItem
                        className="capitalize"
                        key={prod}
                        value={prod}
                      >
                        {prod}
                      </SelectItem>
                    ))}
                  </SelectGroup>
                  <SelectItem key={'manageProducts'} value="manageProducts">
                    Manage Products
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            <TabsContent value="general">
              <DeviceGeneralTab
                selectedProduct={product}
                device={device}
                editing={editingGeneralTab}
                setEditing={setEditingGeneralTab}
                isRefreshingCache={isRefreshingCache}
                deviceRois={roiData?.roi as Roi[]}
                refetchDeviceRois={refetchDeviceRois}
                refetchAuditLog={refetchAuditLog}
                updateDevice={(input) => void handleUpdateDevice(input)}
                onRefreshCache={() => void handleRefreshCache()}
                onSelectedMediaTimestampChange={setSelectedMediaTimestamp}
                onSelectedMediaKindChange={setSelectedMediaKind}
              />
            </TabsContent>
            {hasPermission(FeatureName.VIEW_DEVICE_CONFIG) && (
              <TabsContent value="config">
                <DeviceConfigTab
                  config={device.config}
                  onConfigChange={(config) => void handleConfigChange(config)}
                />
              </TabsContent>
            )}
            {hasPermission(FeatureName.VIEW_API_KEYS) && (
              <TabsContent value="apiKeys">
                <DeviceApiKeysTab
                  deviceId={id as string}
                  apiKeys={device.apiKeys}
                  refetchAuditLog={refetchAuditLog}
                  refetchDevice={refetchDevice}
                ></DeviceApiKeysTab>
              </TabsContent>
            )}
            {hasPermission(FeatureName.VIEW_ASSIGNED_USERS) && (
              <TabsContent value="assignedUsers">
                <DeviceAssignedUserTab
                  deviceId={id as string}
                  refetchAuditLog={refetchAuditLog}
                />
              </TabsContent>
            )}
            {hasPermission(FeatureName.VIEW_AUDIT_HISTORY) && (
              <TabsContent value="auditHistory">
                <AuditHistoryTab auditHistory={auditHistory} />
              </TabsContent>
            )}
          </Tabs>
        </>
      ) : (
        <div className="flex h-full flex-grow items-center justify-center">
          <Spinner />
        </div>
      )}
    </ErrorBoundary>
  );
};

export default EditDevicePage;
